import {
  Directive,
  Optional,
  Inject,
  ViewContainerRef,
  ComponentFactoryResolver,
  ComponentRef,
  Input,
  OnDestroy, ElementRef, AfterViewInit
} from '@angular/core';
import { AbstractControl, NgControl } from '@angular/forms';
import { merge, EMPTY, Observable, Subject, fromEvent, NEVER } from 'rxjs';
import { distinctUntilChanged, mapTo, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { FormControlErrorsContainerDirective } from './form-control-errors-container.directive';
import { FormSubmitDirective } from './form-submit.directive';
import { FormControlErrorsComponent } from './form-control-errors.component';
import { FORM_ERRORS } from './form-errors';

@Directive({
  // tslint:disable-next-line:directive-selector
  selector: '[formControl], [formControlName]'
})
export class FormControlErrorsDirective implements AfterViewInit, OnDestroy {
  @Input() customErrors: any = {};
  @Input() controlErrorsOnBlur = true;
  @Input() controlErrorsOnAsync = true;

  private ref!: ComponentRef<FormControlErrorsComponent>;
  private container: ViewContainerRef;
  private destroy = new Subject();
  private submit$: Observable<Event>;
  private formSubmitted!: boolean;

  constructor(
    @Optional() controlErrorContainer: FormControlErrorsContainerDirective,
    @Optional() private form: FormSubmitDirective,
    @Inject(FORM_ERRORS) private errors: any,
    private vcr: ViewContainerRef,
    private host: ElementRef,
    private resolver: ComponentFactoryResolver,
    private controlDir: NgControl
  ) {
    this.container = controlErrorContainer ? controlErrorContainer.vcr : vcr;
    this.submit$ = this.form ? this.form.submit$ : EMPTY;
  }

  ngAfterViewInit(): void {
    const statusChanges$ = this.control.statusChanges.pipe(distinctUntilChanged());
    const valueChanges$ = this.control.valueChanges;
    const controlChanges$ = merge(statusChanges$, valueChanges$);
    let changesOnBlur$: Observable<any> = EMPTY;

    if (this.controlErrorsOnBlur) {
      const blur$ = fromEvent(this.host.nativeElement, 'focusout');
      changesOnBlur$ = blur$.pipe(switchMap(() => valueChanges$.pipe(startWith(true))));
    }

    const submit$ = this.submit$.pipe(mapTo(true));

    const changesOnSubmit$ = submit$.pipe(
      tap(() => this.formSubmitted = true),
      switchMap(submit => (submit ? controlChanges$.pipe(startWith(true)) : NEVER))
    );

    merge(changesOnBlur$, changesOnSubmit$, this.control.valueChanges)
      .pipe(takeUntil(this.destroy))
      .subscribe(() => this.valueChanges());
  }

  valueChanges(): void {
    const controlErrors = this.control.errors;
    if (controlErrors && (this.controlDir.touched || this.controlDir.dirty)) {
      const controlErrorKeys = Object.keys(controlErrors);
      let text = '';
      controlErrorKeys.forEach(error => {
        const key = error;
        const getError = this.errors[key];
        text = `${text} ${(this.customErrors[key] || getError(controlErrors[key]))}`;
      });
      this.setError(text);
    }
    else if (this.ref) {
      this.setError(null);
    }
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  get control(): AbstractControl {
    return this.controlDir.control as AbstractControl;
  }

  setError(text: string | null): void {
    if (!this.ref) {
      const factory = this.resolver.resolveComponentFactory(FormControlErrorsComponent);
      this.ref = this.container.createComponent(factory);
    }
    this.ref.instance.text = text;
  }
}
